import axiosInstance from "./axiosinstance";

export interface ServiceCall {
  id?: string;
  serviceCallId?: string;

  customerId?: string;
  customerName: string;
  location: string;

  machineTypes: string[];
  softwares: string[];

  visitFor: number;

  servicesRendered: string[];
  partsReplaced: string[];

  engineerRemarks: string[];

  customerRemark: string;

  customerEngineer: string;
  customerEngineerDesignation: string;

  cSignature?: string;
  eSignature?: string;
  customerSignatureUrl?: string;
  engineerSignatureUrl?: string;

  plant: string;

  status?: string;
  reportGenerated?: boolean | string;

  fileUrl?: string;
}

export interface ServiceCallUpdate {
  customerId?: string;
  customerName?: string;
  location?: string;

  machineTypes?: string[];
  softwares?: string[];

  visitFor?: number | null;

  servicesRendered?: string[];
  partsReplaced?: string[];

  engineerRemarks?: string[];

  customerRemark?: string;

  customerEngineer?: string;
  customerEngineerDesignation?: string;

  status?: string;

  cSignature?: string;
  customerSignatureUrl?: string;

  eSignature?: string;
  engineerSignatureUrl?: string;

  plant?: string;

  fileUrl?: string;
}

export interface Service {
  customerName: string;
  address: string;

  serviceCalls: {
    id: string;
    createdBy: string;
    createdAt: string;
    status?: string;
    reportGenerated?: boolean | string;
  }[];
}

export interface CustomerWiseServiceResponse {
  items: Service[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export const buildServiceCallFormData = (
  data: ServiceCallUpdate & {
    CSignatureFile?: File | null;
    ESignatureFile?: File | null;
  }
): FormData => {
  const formData = new FormData();

  if (data.customerId !== undefined) {
    formData.append("CustomerId", data.customerId);
  }

  if (data.customerName !== undefined) {
    formData.append("CustomerName", data.customerName);
  }

  if (data.location !== undefined) {
    formData.append("Location", data.location);
  }

  if (data.visitFor !== undefined && data.visitFor !== null) {
    formData.append("VisitFor", String(data.visitFor));
  }

  if (data.customerRemark !== undefined) {
    formData.append("CustomerRemark", data.customerRemark);
  }

  if (data.customerEngineer !== undefined) {
    formData.append("CustomerEngineer", data.customerEngineer);
  }

  if (data.customerEngineerDesignation !== undefined) {
    formData.append(
      "CustomerEngineerDesignation",
      data.customerEngineerDesignation
    );
  }

  if (data.plant !== undefined) {
    formData.append("Plant", data.plant);
  }

  /* ============================================================
     STATUS
     ============================================================ */

  if (data.status !== undefined) {
    formData.append("Status", data.status);
  }

  /* ============================================================
     MACHINE TYPES
     ============================================================ */

  if (data.machineTypes !== undefined) {
    data.machineTypes.forEach((machineType) => {
      if (
        machineType !== undefined &&
        machineType !== null &&
        String(machineType).trim() !== ""
      ) {
        formData.append("MachineTypes", String(machineType));
      }
    });
  }

  /* ============================================================
     SOFTWARES
     ============================================================ */

  if (data.softwares !== undefined) {
    data.softwares.forEach((software) => {
      if (
        software !== undefined &&
        software !== null &&
        String(software).trim() !== ""
      ) {
        formData.append("Softwares", String(software));
      }
    });
  }

  /* ============================================================
     SERVICES RENDERED
     ============================================================ */

  if (data.servicesRendered !== undefined) {
    data.servicesRendered.forEach((service) => {
      if (
        service !== undefined &&
        service !== null &&
        String(service).trim() !== ""
      ) {
        formData.append("ServicesRendered", String(service));
      }
    });
  }

  /* ============================================================
     PARTS REPLACED
     ============================================================ */

  if (data.partsReplaced !== undefined) {
    data.partsReplaced.forEach((part) => {
      if (
        part !== undefined &&
        part !== null &&
        String(part).trim() !== ""
      ) {
        formData.append("PartsReplaced", String(part));
      }
    });
  }

  /* ============================================================
     ENGINEER REMARKS
     ============================================================ */

  if (data.engineerRemarks !== undefined) {
    data.engineerRemarks.forEach((remark) => {
      if (
        remark !== undefined &&
        remark !== null &&
        String(remark).trim() !== ""
      ) {
        formData.append("EngineerRemarks", String(remark));
      }
    });
  }

  /* ============================================================
     CUSTOMER SIGNATURE FILE
     ============================================================ */

  if (data.CSignatureFile instanceof File) {
    formData.append(
      "CSignatureFile",
      data.CSignatureFile,
      data.CSignatureFile.name
    );
  }

  /* ============================================================
     ENGINEER SIGNATURE FILE
     ============================================================ */

  if (data.ESignatureFile instanceof File) {
    formData.append(
      "ESignatureFile",
      data.ESignatureFile,
      data.ESignatureFile.name
    );
  }

  /* ============================================================
     CUSTOMER SIGNATURE URL

     IMPORTANT:
     Backend can return a FULL URL here.

     Example:
     https://server.com/customers/.../customer-signature
     ============================================================ */

  if (
    data.cSignature !== undefined &&
    data.cSignature !== null &&
    data.cSignature.trim() !== ""
  ) {
    formData.append("CSignature", data.cSignature);
  }

  /* ============================================================
     ENGINEER SIGNATURE URL

     IMPORTANT:
     Backend can return a FULL URL here.
     ============================================================ */

  if (
    data.eSignature !== undefined &&
    data.eSignature !== null &&
    data.eSignature.trim() !== ""
  ) {
    formData.append("ESignature", data.eSignature);
  }

  /* ============================================================
     FILE URL
     ============================================================ */

  if (
    data.fileUrl !== undefined &&
    data.fileUrl !== null &&
    data.fileUrl.trim() !== ""
  ) {
    formData.append("FileUrl", data.fileUrl);
  }

  return formData;
};

/* ============================================================
   SERVICE CALL REPOSITORY
   ============================================================ */

const serviceCallRepository = {
  /* ============================================================
     GET ALL SERVICE CALLS

     GET /api/servicecalls
     ============================================================ */

  getAll: async (
    pageNumber: number = 1,
    pageSize: number = 20,
    search: string = ""
  ): Promise<ServiceCall[]> => {
    const response = await axiosInstance.get<ServiceCall[]>(
      "/api/servicecalls",
      {
        params: {
          pageNumber,
          pageSize,
          search,
        },
      }
    );

    return response.data;
  },

  /* ============================================================
     GET CUSTOMER-WISE SERVICE CALLS

     GET /api/servicecalls/customer-wise
     ============================================================ */

  getservicecall: async (
    pageNumber: number = 1,
    pageSize: number = 20,
    search: string = ""
  ): Promise<CustomerWiseServiceResponse> => {
    const response = await axiosInstance.get(
      "/api/servicecalls/customer-wise",
      {
        params: {
          pageNumber,
          pageSize,
          search,
        },
      }
    );

    const data = response.data;

    console.log("Customer-wise service call API response:", data);

    /* ============================================================
       API DIRECTLY RETURNS ARRAY
       ============================================================ */

    if (Array.isArray(data)) {
      return {
        items: data,
        totalCount: data.length,
        pageNumber,
        pageSize,
        totalPages: Math.ceil(data.length / pageSize),
      };
    }

    /* ============================================================
       API RETURNS PAGINATED OBJECT
       ============================================================ */

    const totalCount = data?.totalCount ?? data?.total ?? data?.count ?? 0;

    const currentPage = data?.pageNumber ?? pageNumber;

    const currentPageSize = data?.pageSize ?? pageSize;

    return {
      items:
        data?.items ??
        data?.data ??
        data?.results ??
        data?.serviceCalls ??
        [],

      totalCount,

      pageNumber: currentPage,

      pageSize: currentPageSize,

      totalPages: data?.totalPages ?? Math.ceil(totalCount / currentPageSize),
    };
  },

  /* =======================================================
     ADD SERVICE CALL
     POST
     multipart/form-data
     ======================================================= */

  add: async (formData: FormData): Promise<ServiceCall> => {
    const response = await axiosInstance.post<ServiceCall>(
      "/api/servicecalls",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response.data;
  },

  /* =======================================================
     GET SERVICE CALL BY ID

     GET /api/servicecalls/{id}
     ======================================================= */

  getById: async (id: string): Promise<ServiceCall> => {
    const response = await axiosInstance.get<ServiceCall>(
      `/api/servicecalls/${id}`
    );
    const serviceCall = response.data as ServiceCall & {
      ReportGenerated?: boolean;
    };

    return {
      ...serviceCall,
      reportGenerated:
        serviceCall.reportGenerated ?? serviceCall.ReportGenerated,
    };
  },

  /* =======================================================
     UPDATE SERVICE CALL
     PATCH multipart/form-data

     PATCH /api/servicecalls/{id}
     ======================================================= */

  update: async (
    id: string,
    data: ServiceCallUpdate & {
      CSignatureFile?: File | null;
      ESignatureFile?: File | null;
    }
  ): Promise<ServiceCall> => {
    const formData = buildServiceCallFormData(data);

    const response = await axiosInstance.patch<ServiceCall>(
      `/api/servicecalls/${id}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response.data;
  },

  /* =======================================================
     DELETE SERVICE CALL

     DELETE /api/servicecalls/{id}
     ======================================================= */

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`/api/servicecalls/${id}`);
  },

  /* =======================================================
     DOWNLOAD SERVICE CALL REPORT (PDF)

     GET /api/servicecalls/{id}/report
     ======================================================= */

  download: async (id: string): Promise<Blob> => {
    const response = await axiosInstance.get(
      `/api/servicecalls/${id}/report`,
      {
        responseType: "blob",
      }
    );

    return response.data;
  },

  sendReportEmail: async (serviceCallId: string): Promise<void> => {
    await axiosInstance.post(
      "/api/servicecalls/send-report-email",
      null,
      {
        params: {
          serviceCallId,
          type: "Servicecallreport",
        },
      }
    );
  },

  /* =======================================================
     DOWNLOAD A STORED SERVICE CALL FILE (signature image)

     GET /api/servicecalls/download?fileUrl=...
     ======================================================= */

  downloadFile: async (fileUrl: string): Promise<Blob> => {
    const response = await axiosInstance.get("/api/servicecalls/download", {
      params: {
        fileUrl,
      },
      responseType: "blob",
    });

    return response.data;
  },

  /* =======================================================
     DOWNLOAD A USER'S SAVED SIGNATURE (Profile Settings)

     GET /api/servicecalls/download?userId=...
     Returns the user's signature.png as an image Blob.
     ======================================================= */

  downloadUserSignature: async (userId: string): Promise<Blob> => {
    const response = await axiosInstance.get("/api/servicecalls/download", {
      params: {
        userId,
      },
      responseType: "blob",
    });

    return response.data;
  },
};

export { serviceCallRepository };

export default serviceCallRepository;